import { Injectable } from '@angular/core';
import { Amplify } from 'aws-amplify';
import { signIn, fetchAuthSession, signOut, resetPassword, confirmResetPassword, AuthTokens, confirmSignIn } from '@aws-amplify/auth';
import { AccessService } from '@app-core/services/access/access.service';
import { ErrorKeys } from './constants';
import { ResellerConfig } from '@reseller-config/reseller-config';
import { CLIENT_CONFIG } from '@config/config';
import { SnackBarService } from '@app-core/services/snackbar/snack-bar.service';
import { TranslateService } from '@ngx-translate/core';
import { environment } from '@env/environment';
import * as Sentry from '@sentry/angular';
import { API } from '@app-core/constants/api.constants';
import { HttpClient } from '@angular/common/http';
import { take } from 'rxjs/operators';
import { DEFAULT_LANGUAGE } from '@app-core/constants/constants';
import { LoginPageUtilService } from '../login-page-util.service';

@Injectable({
  providedIn: 'root',
})
export class AmplifyAuthService {
  public clientName = CLIENT_CONFIG.clientName;
  private selectedLanguage = DEFAULT_LANGUAGE;

  constructor(
    private accessService: AccessService,
    private snackbarService: SnackBarService,
    private translate: TranslateService,
    private http: HttpClient,
    private loginPageUtilService: LoginPageUtilService
  ) {}

  configureAWSAmplify() {
    const { userPoolId, userPoolClientId } = this.getUserPoolDetails();

    Amplify.configure({
      Auth: {
        Cognito: {
          userPoolId: userPoolId,
          userPoolClientId: userPoolClientId,
        },
      },
    });
  }

  private getUserPoolDetails() {
    const region = this.accessService.getAwsCognitoRegion();
    const { userPoolId = '', userPoolClientId = '' } = ResellerConfig[region] || {};

    return {
      userPoolId,
      userPoolClientId,
    };
  }

  async signIn(username: string, password: string): Promise<{ signInStep: string; tokens: AuthTokens }> {
    try {
      // force signout if the user is already signed in
      await this.logout();
      this.selectedLanguage = this.loginPageUtilService.getCurrentLanguage();
      const metadata = {
        source: 'customLogin',
        customer: this.clientName,
        language: this.selectedLanguage,
      };
      const user = await signIn({ username, password, options: { clientMetadata: { ...metadata } } });
      const {
        isSignedIn,
        nextStep: { signInStep },
      } = user || {};

      if (isSignedIn && signInStep === 'DONE') {
        const session = await fetchAuthSession();
        const tokens = session.tokens;
        return { signInStep, tokens };
      } else if (
        !isSignedIn &&
        (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED' || signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE')
      ) {
        return { signInStep, tokens: null };
      }
    } catch (err) {
      throw this.handleError(err, username);
    }
  }

  async confirmSignIn(confirmSignInInput: string) {
    try {
      const user = await confirmSignIn({ challengeResponse: confirmSignInInput });
      const {
        isSignedIn,
        nextStep: { signInStep },
      } = user || {};
      if (isSignedIn && signInStep === 'DONE') {
        const session = await fetchAuthSession();
        const tokens = session.tokens;
        return { signInStep, tokens };
      }
    } catch (err) {
      throw this.handleError(err);
    }
  }

  async forgotPassword(username: string) {
    this.selectedLanguage = this.loginPageUtilService.getCurrentLanguage();
    try {
      await resetPassword({ username, options: { clientMetadata: { language: this.selectedLanguage } } });
    } catch (error) {
      throw this.handleError(error, username);
    }
  }

  async handleSubmitNewPassword(username: string, code: string, newPassword: string) {
    try {
      await confirmResetPassword({ username, confirmationCode: code, newPassword });
    } catch (error) {
      throw this.handleError(error, username);
    }
  }

  async logout(attempt = 0) {
    try {
      await signOut();
    } catch (error) {
      if (error.name === 'AuthUserPoolException' && attempt < 3) {
        this.configureAWSAmplify();
        return this.logout(attempt + 1); // Retry logout after reconfiguring
      }

      const err = this.handleError(error);
      console.error('Error during logout:', err);
      const message = err?.customMessage || 'customLoginSignOutFailed';
      this.snackbarService.failure(this.translate.instant(message));
    }
  }

  handleError(error, username?: string) {
    if (ErrorKeys.includes(error.name)) {
      error.customMessage = 'customLoginPageError.' + error.name;
    }

    const metadata = {
      isError: true,
      errorMessage: `${error?.name}: ${error?.message}`,
      statusCode: error?.metadata?.httpStatusCode,
      region: this.accessService.getAwsCognitoRegion(),
      client: this.clientName,
      ...(username && { username }),
    };

    // Log the error to Sentry
    if (environment?.env === 'prod') {
      Sentry.captureException(error, {
        tags: { module: 'AmplifyAuthService' },
        extra: {
          ...metadata,
        },
      });
    }

    //Log error in access.logs
    this.logError(metadata);
    return error;
  }

  logError(metadata: any) {
    const url = API.LOGIN_LOGS;
    this.http
      .post(url, metadata)
      .pipe(take(1))
      .subscribe(
        () => {},
        () => {}
      );
  }
}
